import {
  AdminApiError,
  type AdminClient,
  type GreedykingMultipliersOverride,
} from '@apx/admin-sdk';
import { prettyGame } from '../games.js';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';
import { greedykingMultipliersSchema } from '@apx/integrator-config/schemas';
import { showToast } from '../toast.js';

export interface MultipliersPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
}

const FOOD_ORDER: (keyof GreedykingMultipliersOverride)[] = [
  'carrots',
  'pakcoi',
  'corn',
  'tomato',
  'hotdog',
  'bbq',
  'meat',
  'steak',
];

const CATEGORY: Record<keyof GreedykingMultipliersOverride, 'veg' | 'meat'> = {
  carrots: 'veg',
  pakcoi: 'veg',
  corn: 'veg',
  tomato: 'veg',
  hotdog: 'meat',
  bbq: 'meat',
  meat: 'meat',
  steak: 'meat',
};

// Built-in defaults — kept in sync with packages/games/greedyking/src/constants.ts.
const DEFAULT_VALUE: GreedykingMultipliersOverride = {
  carrots: 5,
  pakcoi: 5,
  corn: 5,
  tomato: 5,
  hotdog: 10,
  bbq: 15,
  meat: 25,
  steak: 45,
};

// Hand-picked default weights for the dry-run RTP sim — mirrors
// DEFAULT_WEIGHTS.primary so admins see a sensible starting baseline
// before fetching the live weights surface.
const PRIMARY_WEIGHTS: Record<keyof GreedykingMultipliersOverride, number> = {
  carrots: 0.18,
  pakcoi: 0.18,
  corn: 0.18,
  tomato: 0.18,
  hotdog: 0.07,
  bbq: 0.045,
  meat: 0.025,
  steak: 0.015,
};
const HOUSE_WEIGHT = 0.072;

function rowHtml(food: keyof GreedykingMultipliersOverride): string {
  const cat = CATEGORY[food];
  const colour = cat === 'veg' ? 'var(--accent-green)' : 'var(--accent-red)';
  return `<tr>
    <td><span class="pill" style="background:${colour};color:#fff">${cat}</span> <strong>${food}</strong></td>
    <td><input data-food="${food}" type="number" min="1" max="1000" step="1" style="width:80px" /></td>
    <td class="muted" data-default="${food}">default ${DEFAULT_VALUE[food]}×</td>
  </tr>`;
}

export async function renderMultipliersPage(
  root: HTMLElement,
  deps: MultipliersPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Payout Multipliers — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">Per-food payout multipliers applied to a winning primary match: <code>basePayout = betAmount × multiplier</code>.</p>

    <div class="card">
      <h2>Multipliers</h2>
      <table>
        <thead><tr><th>Food</th><th>Multiplier</th><th></th></tr></thead>
        <tbody id="mu-rows">${FOOD_ORDER.map(rowHtml).join('')}</tbody>
      </table>
      <div class="row" style="margin-top:14px">
        <button id="mu-save" class="primary">Propose change</button>
        <button id="mu-reset" class="ghost">Reset to defaults</button>
      </div>
      <p id="mu-msg" class="muted"></p>
    </div>

    <div class="card">
      <h2>Dry-run RTP simulator</h2>
      <p class="muted" style="margin-top:-4px">Theoretical RTP using the proposed multiplier table against the default cabin weight distribution. Match bonuses + special-tier are excluded — this is the primary-only RTP baseline. Aim ≤ 0.95 unless intentional.</p>
      <div class="row">
        <button id="mu-sim">Run simulation</button>
        <span id="mu-sim-result" class="muted" style="margin-left:12px"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="mu-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const rowsEl = root.querySelector<HTMLElement>('#mu-rows')!;
  const msgEl = root.querySelector<HTMLElement>('#mu-msg')!;
  const simResultEl = root.querySelector<HTMLElement>('#mu-sim-result')!;

  function readForm(): GreedykingMultipliersOverride {
    const out: Partial<GreedykingMultipliersOverride> = {};
    for (const food of FOOD_ORDER) {
      const input = rowsEl.querySelector<HTMLInputElement>(`input[data-food="${food}"]`);
      out[food] = input ? Number(input.value) || 0 : 0;
    }
    return out as GreedykingMultipliersOverride;
  }

  function fillForm(value: GreedykingMultipliersOverride): void {
    for (const food of FOOD_ORDER) {
      const input = rowsEl.querySelector<HTMLInputElement>(`input[data-food="${food}"]`);
      if (input) input.value = String(value[food]);
    }
  }

  const { value: current } = await deps.client.getSurface(
    deps.integratorId,
    deps.gameId,
    'multipliers',
  );
  fillForm((current as GreedykingMultipliersOverride | null) ?? DEFAULT_VALUE);

  root.querySelector<HTMLButtonElement>('#mu-reset')!.addEventListener('click', () => {
    fillForm(DEFAULT_VALUE);
    msgEl.textContent = 'Reset to defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#mu-sim')!.addEventListener('click', () => {
    const v = readForm();
    let rtp = 0;
    for (const food of FOOD_ORDER) {
      rtp += PRIMARY_WEIGHTS[food] * v[food];
    }
    // House cabin contributes 0 payout, just absorbs probability mass.
    void HOUSE_WEIGHT;
    const colour = rtp > 0.97 ? 'var(--accent-red)' : rtp > 0.92 ? 'var(--accent-amber)' : 'var(--accent-green)';
    simResultEl.innerHTML =
      `Theoretical primary RTP: <strong style="color:${colour}">${(rtp * 100).toFixed(2)}%</strong>`;
  });

  root.querySelector<HTMLButtonElement>('#mu-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const value = readForm();
    const parsed = greedykingMultipliersSchema.safeParse(value);
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'multipliers',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'multipliers' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#mu-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th>Pending</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td><code>${JSON.stringify(a.pendingConfig)}</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'multipliers');
          fillForm((fresh.value as GreedykingMultipliersOverride | null) ?? DEFAULT_VALUE);
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
  }

  await refreshPending();
}
